import * as React from 'react';

import { useModulesData } from '@/features/modules/api/queries';
import { useProfilesData } from '@/features/user';
import { useNotificationsDrawerStore } from '@/stores/notifications';

import { getEnabledNotifications } from '../config/notification-config';
import type { NotificationConfig } from '../config/notification-config';

export type NotificationsData = {
  // Processed/derived data
  enabledNotifications: NotificationConfig[];
  hasNotifications: boolean;
  notificationCards: React.ReactNode[];

  // Loading and error states
  isLoading: boolean;
  hasError: boolean;
  loadingStates: {
    modules: boolean;
    profile: boolean;
  };
  errorStates: {
    modules: boolean;
    profile: boolean;
  };
};

/**
 * Custom hook that consolidates all data requirements for the Notification system.
 * Combines data collection and processing into a single source of truth.
 */
export function useNotificationsData(): NotificationsData {
  // Core data sources
  const {
    courseAggregate,
    allSessions,
    isLoading: isLoadingModules,
    isError: isErrorModules,
  } = useModulesData();

  const {
    selectedProfile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useProfilesData();

  // Zustand store actions
  const setOpenSchoolCurriculum = useNotificationsDrawerStore(
    state => state.setOpenSchoolCurriculum
  );
  const setOpenNaplan = useNotificationsDrawerStore(
    state => state.setOpenNaplan
  );
  const setOpenIMO = useNotificationsDrawerStore(state => state.setOpenIMO);

  // Consolidated loading and error states
  const isLoading = isLoadingModules || isLoadingProfile;
  const hasError = isErrorModules || isErrorProfile;

  // Data processing - must happen before any early returns to satisfy rules of hooks
  // 1. Get enabled notifications based on current data (empty array if loading)
  const enabledNotifications = React.useMemo(
    () =>
      isLoading
        ? []
        : getEnabledNotifications({
            courseAggregate,
            enrolledCourses: selectedProfile?.enrolled_courses,
            selectedProfileId: selectedProfile?.id,
            selectedVariantId: selectedProfile?.selected_variant_id,
            country: selectedProfile?.country,
            allSessions,
          }),
    [
      isLoading,
      courseAggregate,
      selectedProfile?.enrolled_courses,
      selectedProfile?.id,
      selectedProfile?.selected_variant_id,
      selectedProfile?.country,
      allSessions,
    ]
  );

  // 2. Check if we have any notifications
  const hasNotifications = enabledNotifications.length > 0;

  // 3. Build notification cards - each card is self-contained and handles its own modal
  const notificationCards = React.useMemo(
    () => enabledNotifications.map(config => config.getCard(config.id)),
    [enabledNotifications]
  );

  // 4. Update notification states based on enabled notifications
  React.useEffect(() => {
    if (!isLoading) {
      const shouldShowSchoolCurriculum = enabledNotifications.some(
        config => config.id === 'school-curriculum'
      );
      const shouldShowNaplan = enabledNotifications.some(
        config => config.id === 'naplan'
      );
      const shouldShowIMO = enabledNotifications.some(
        config => config.id === 'imo'
      );
      setOpenSchoolCurriculum(shouldShowSchoolCurriculum);
      setOpenNaplan(shouldShowNaplan);
      setOpenIMO(shouldShowIMO);
    }
  }, [
    isLoading,
    enabledNotifications,
    setOpenSchoolCurriculum,
    setOpenNaplan,
    setOpenIMO,
  ]);

  return {
    enabledNotifications,
    hasNotifications,
    notificationCards,
    isLoading,
    hasError,
    loadingStates: {
      modules: isLoadingModules,
      profile: isLoadingProfile,
    },
    errorStates: {
      modules: isErrorModules,
      profile: isErrorProfile,
    },
  };
}
